if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };
        this.isSubmitting = false;
        this.canSubmit = false;
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        if (!this._clickHandler) {
          this._clickHandler = this.onClick.bind(this);
          this.submitEl?.addEventListener('click', this._clickHandler);
        }
        this.productFormActions =
          document.getElementById(this.dataset.rnFormId) || document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.canSubmit = Boolean(
          this.submitEl &&
            !this.submitEl.disabled &&
            this.submitEl.dataset.rnConfigurationBlocked !== 'true'
        );

        this.init();

        if (this.productFormActions && !this._personalisationInvalidHandler) {
          this._personalisationInvalidHandler = () => this.resetButtonLoading();
          this.productFormActions.addEventListener(
            'rn:personalisation-invalid',
            this._personalisationInvalidHandler
          );
        }

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        if (this._syncVisibility) {
          window.removeEventListener('scroll', this._syncVisibility);
          window.visualViewport?.removeEventListener('resize', this._syncVisibility);
        }
        if (this.productFormActions && this._personalisationInvalidHandler) {
          this.productFormActions.removeEventListener(
            'rn:personalisation-invalid',
            this._personalisationInvalidHandler
          );
          this._personalisationInvalidHandler = null;
        }
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        // Show the bar once the main Add to basket button has scrolled off the top, on every
        // product layout. (The old check waited for the whole form to fit on screen, which a
        // tall form never does, so the bar stayed hidden.) It steps back for the footer, the
        // basket drawer and the on-screen keyboard.
        const mainButton = () =>
          this.productFormActions.querySelector('[type="submit"][name="add"]') ||
          this.mainProductInfo?.querySelector('[type="submit"][name="add"]');
        const footer = document.querySelector('footer, .shopify-section-group-footer-group');
        let footerVisible = false;
        this._syncVisibility = () => {
          const rect = mainButton()?.getBoundingClientRect();
          const drawer = document.querySelector('cart-drawer[open], .cart-drawer.is-open, .drawer[open]');
          const keyboard = window.visualViewport && window.visualViewport.height < window.innerHeight * 0.72;
          this.classList.toggle(
            this.classes.isOpen,
            Boolean(rect && rect.height && rect.bottom <= 0 && !footerVisible && !drawer && !keyboard)
          );
        };
        if (footer && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            footerVisible = entries.some((entry) => entry.isIntersecting);
            this._syncVisibility();
          });
          this.observer.observe(footer);
        }
        window.addEventListener('scroll', this._syncVisibility, { passive: true });
        window.visualViewport?.addEventListener('resize', this._syncVisibility);
        this._syncVisibility();
        this.syncWithMainProductForm();
      }

      onClick(event) {
        if (this.isSubmitting || !this.canSubmit) {
          event.preventDefault();
          return;
        }

        this.isSubmitting = true;
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.dataset.rnSubmitting = 'true';
        this.submitEl.classList.add('btn--loading');

        // A native required field can stop submission before submit fires.
        // Release the lock on the next frame so the customer can correct it.
        requestAnimationFrame(() => {
          if (this.productFormActions && !this.productFormActions.checkValidity()) {
            this.resetButtonLoading();
          }
        });
      }

      resetButtonLoading() {
        this.isSubmitting = false;
        this.submitEl.removeAttribute('aria-disabled');
        delete this.submitEl.dataset.rnSubmitting;
        this.submitEl.classList.remove('btn--loading');
        this.submitEl.disabled = !this.canSubmit;
      }

      scrollToTop(target, offset = 200) {
        if (!target) return;
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        const configurationBlocked = addButton.dataset.rnConfigurationBlocked === 'true';
        this.canSubmit = !disable && !configurationBlocked;
        if (disable || configurationBlocked) {
          addButton.disabled = true;
          if (addButtonText) {
            addButtonText.textContent = configurationBlocked
              ? 'Personalisation unavailable'
              : text || FoxTheme.variantStrings.soldOut;
          }
        } else {
          addButton.disabled = this.isSubmitting;
          if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = this.mainProductInfo && event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
